import React, { ButtonHTMLAttributes } from 'react'

export interface PropsBtn<T> extends ButtonHTMLAttributes<T> {
  color?: 'green' | 'pink' | 'white' | 'blue'
  fontSize?: 'small' | 'normal' | 'large'
  width?: 'small' | 'medium' | 'large'
  isVisible?: boolean
  children?: React.ReactNode
}

type Props = PropsBtn<HTMLButtonElement>

const Button: React.FC<Props> = ({
  children,
  isVisible = true,
  fontSize,
  width,
  color = 'white',
  className,
  ...rest
}) => {
  const isDisabled = !!rest.disabled

  const colorClasses: Record<NonNullable<Props['color']>, string> = {
    green:
      'bg-[linear-gradient(60deg,#6DE381,#31D19E)] text-[#F5F5F5] shadow-[0px_2px_4px_rgba(0,0,0,0.08),0px_4px_8px_rgba(61,69,67,0.08)]',
    blue: 'bg-[linear-gradient(15deg,#2F80ED,#56CCF2)] text-[#F5F5F5] shadow-[0px_2px_4px_rgba(0,0,0,0.08),0px_4px_8px_rgba(61,69,67,0.08)]',
    pink: 'bg-[linear-gradient(60deg,#e283a7,#EB6A9F)] text-[#F5F5F5] shadow-[0px_2px_4px_rgba(0,0,0,0.08),0px_4px_8px_rgba(61,69,67,0.08)]',
    white:
      'bg-white text-[#333] shadow-[0px_2px_4px_rgba(0,0,0,0.08),0px_4px_8px_rgba(61,69,67,0.08)]',
  }

  const fontSizeClasses: Record<NonNullable<Props['fontSize']>, string> = {
    small: 'min-h-[40px] text-[12px] font-normal',
    normal: 'min-h-[50px] text-[16px] font-bold',
    large: 'min-h-[50px] text-[18px] font-bold',
  }

  const widthClasses: Record<NonNullable<Props['width']>, string> = {
    small: 'w-[180px] px-[12px] py-[8px]',
    medium: 'w-[200px] px-[12px] py-[8px]',
    large: 'w-[250px] px-[12px] py-[8px]',
  }

  const disabledClasses =
    'bg-[#bababa] text-[#F5F5F5] shadow-[0px_6px_8px_rgba(101,101,101,0.09)] border border-[#eaeaea] cursor-not-allowed'

  const computedClassName = [
    'border-0 rounded-[30px] px-10 text-center my-[10px] flex items-center justify-around focus:underline',
    fontSizeClasses[fontSize || 'normal'],
    widthClasses[width || 'medium'],
    isDisabled ? disabledClasses : colorClasses[color],
    isDisabled ? 'cursor-not-allowed' : 'cursor-pointer',
    '[&>svg]:mr-[4px] [&>svg]:max-w-[35px]',
    className || '',
  ]
    .filter(Boolean)
    .join(' ')

  return isVisible ? (
    <button type="button" className={computedClassName} {...rest}>
      {children}
    </button>
  ) : null
}

export default Button
